import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Check, ShieldCheck, Lock } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, Badge, Button, Toast } from '../components/ds';
import CompanyOnboardingForm from '../components/CompanyOnboardingForm';
import { paymentService } from '../services';
import { useIsMobile } from '../hooks/useMediaQuery';
import { formatPlanPrice, formatPriceSuffix } from '../utils/plans';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const BRAND = '#1E4FBE';

// Free vs. Paid Gegenüberstellung (AUD-58): Diagnose ist frei, die Umsetzung
// (Maßnahmen, Folge-Audits, weitere Frameworks) ist der kostenpflichtige Mehrwert.
// Namen, Preise und Leistungsmerkmale kommen aus GET /payments/plans (AUD-61) —
// im Frontend steht kein Preis mehr.

const STATUS_LABEL = {
  trialing: { tone: 'info', text: 'Testphase' },
  active: { tone: 'secure', text: 'Abo aktiv' },
  past_due: { tone: 'warning', text: 'Zahlung fehlgeschlagen' },
  cancelled: { tone: 'critical', text: 'Gekündigt' },
  none: { tone: 'neutral', text: 'Free' },
};

function formatDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch {
    return null;
  }
}

const FeatureList = ({ features, icon }) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: '20px 0', display: 'flex', flexDirection: 'column', gap: 11 }}>
    {features.map((f) => (
      <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: '#2A3140', lineHeight: 1.45 }}>
        {icon}
        {f}
      </li>
    ))}
  </ul>
);

function PricingPage() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [toast, setToast] = useState(null);

  const cancelled = new URLSearchParams(location.search).get('checkout') === 'cancelled';

  const loadSubscription = useCallback(async () => {
    try {
      const [subRes, plansRes] = await Promise.all([
        paymentService.getSubscription(),
        paymentService.getPlans(),
      ]);
      setSub(subRes.data);
      setPlans(plansRes.data?.plans || []);
    } catch {
      setToast({ tone: 'critical', message: 'Tarife konnten nicht geladen werden.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const startCheckout = async () => {
    setRedirecting(true);
    try {
      const { data } = await paymentService.createCheckoutSession();
      window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.error || 'Checkout konnte nicht gestartet werden.';
      setToast({ tone: 'critical', message: msg });
      setRedirecting(false);
    }
  };

  const openPortal = async () => {
    setRedirecting(true);
    try {
      const { data } = await paymentService.createPortalSession();
      window.location.href = data.url;
    } catch (err) {
      const msg = err?.response?.data?.error || 'Verwaltungsportal konnte nicht geöffnet werden.';
      setToast({ tone: 'critical', message: msg });
      setRedirecting(false);
    }
  };

  const hasAccess = sub?.hasAccess;
  const status = sub?.status || 'none';
  const statusLabel = STATUS_LABEL[status] || STATUS_LABEL.none;
  const periodEnd = formatDate(sub?.currentPeriodEnd);
  // Der Katalog enthält genau einen kostenpflichtigen Plan (siehe config/plans.js);
  // die Gegenüberstellung hier ist bewusst auf Free vs. Paid zugeschnitten. Die
  // vollständige Plan-Übersicht liegt unter Einstellungen → Abonnement (AUD-61).
  const freePlan = plans.find((p) => !p.purchasable);
  const paidPlan = plans.find((p) => p.purchasable);
  // Checkout ist company-first: ohne angelegtes Unternehmen kein Abo. Neue Nutzer
  // legen hier zuerst ihr Unternehmen an (kein Trial mehr — direkter Abschluss).
  const needsCompany = sub && sub.hasCompany === false && !hasAccess;

  return (
    <Layout>
      <div style={{ padding: isMobile ? '24px 16px 40px' : '36px 40px 48px', maxWidth: 860, margin: '0 auto', fontFamily: FONT }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: '#101623', letterSpacing: '-0.011em' }}>Tarife & Abrechnung</h1>
        <p style={{ margin: '0 0 28px', fontSize: 13, color: '#8A93A3' }}>Diagnose kostenlos — Umsetzung mit Audicio Standard</p>

        {cancelled && !hasAccess && (
          <div style={{ marginBottom: 20 }}>
            <Card padding={16} style={{ background: 'var(--warn-50)', border: '1px solid var(--warn-100)' }}>
              <span style={{ fontSize: 13, color: 'var(--warn-700)' }}>
                Der Checkout wurde abgebrochen. Sie können jederzeit erneut starten.
              </span>
            </Card>
          </div>
        )}

        {loading ? (
          <p style={{ fontSize: 13, color: '#8A93A3' }}>Wird geladen …</p>
        ) : !freePlan || !paidPlan ? (
          <Card padding={22}>
            <p style={{ margin: '0 0 14px', fontSize: 13, color: '#C22B31' }}>Die Tarife konnten nicht geladen werden.</p>
            <Button size="md" onClick={loadSubscription}>Erneut versuchen</Button>
          </Card>
        ) : needsCompany ? (
          <CompanyOnboardingForm
            title="Zuerst Ihr Unternehmen"
            subtitle={`Legen Sie Ihr Unternehmensprofil an, um anschließend ${paidPlan.name} zu buchen.`}
            submitLabel="Weiter"
            onCreated={loadSubscription}
          />
        ) : hasAccess ? (
          // ── Verwaltungsansicht: aktives Abo ──
          <Card padding={28}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <ShieldCheck size={20} color="var(--brand-600)" />
              <span style={{ fontSize: 18, fontWeight: 700, color: '#101623' }}>{paidPlan.name}</span>
              <Badge tone={statusLabel.tone} dot>{statusLabel.text}</Badge>
            </div>
            <div style={{ fontSize: 14, color: '#4A5262' }}>
              <span style={{ fontSize: 28, fontWeight: 700, color: '#101623' }}>{formatPlanPrice(paidPlan.price)}</span>
              <span style={{ marginLeft: 6 }}>{formatPriceSuffix(paidPlan.price)}</span>
            </div>

            <FeatureList
              features={paidPlan.features}
              icon={<Check size={18} color="var(--secure-600)" style={{ flexShrink: 0, marginTop: 1 }} />}
            />

            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 20 }}>
              {sub?.cancelAtPeriodEnd && periodEnd ? (
                <p style={{ fontSize: 13, color: 'var(--warn-700)', margin: '0 0 16px' }}>
                  Ihr Abo ist gekündigt und läuft noch bis zum {periodEnd}. Danach fällt Ihr Konto auf den Free-Tier zurück.
                </p>
              ) : periodEnd ? (
                <p style={{ fontSize: 13, color: '#4A5262', margin: '0 0 16px' }}>
                  Nächste Verlängerung am {periodEnd}.
                </p>
              ) : null}
              <Button variant="secondary" onClick={openPortal} disabled={redirecting}>
                {redirecting ? 'Wird geöffnet …' : 'Abo verwalten'}
              </Button>
            </div>
          </Card>
        ) : (
          // ── Free/Paid-Vergleich für den Free-Tier ──
          <>
            {status === 'past_due' && (
              <div style={{ marginBottom: 20 }}>
                <Card padding={16} style={{ background: 'var(--warn-50)', border: '1px solid var(--warn-100)' }}>
                  <span style={{ fontSize: 13, color: 'var(--warn-700)' }}>
                    Ihre letzte Zahlung ist fehlgeschlagen. Bitte aktualisieren Sie Ihre Zahlungsdaten im Verwaltungsportal.
                  </span>
                </Card>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, alignItems: 'stretch' }}>
              {/* Free */}
              <Card padding={26}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 17, fontWeight: 700, color: '#101623' }}>{freePlan.name}</span>
                  <Badge tone="neutral">Ihr aktueller Tarif</Badge>
                </div>
                <div style={{ fontSize: 14, color: '#4A5262' }}>
                  <span style={{ fontSize: 26, fontWeight: 700, color: '#101623' }}>{formatPlanPrice(freePlan.price)}</span>
                  <span style={{ marginLeft: 6 }}>{formatPriceSuffix(freePlan.price)}</span>
                </div>
                <FeatureList
                  features={freePlan.features}
                  icon={<Check size={18} color="var(--secure-600)" style={{ flexShrink: 0, marginTop: 1 }} />}
                />
              </Card>

              {/* Paid */}
              {/* overflow: visible — sonst kappt die Card das überstehende Ribbon. */}
              <Card padding={26} style={{ border: `1.5px solid ${BRAND}`, position: 'relative', overflow: 'visible' }}>
                <div style={{
                  position: 'absolute', top: -11, right: 20,
                  background: BRAND, color: '#fff', fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.04em', padding: '3px 10px', borderRadius: 999,
                  textTransform: 'uppercase', lineHeight: '16px',
                }}>Empfohlen</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <ShieldCheck size={19} color="var(--brand-600)" />
                  <span style={{ fontSize: 17, fontWeight: 700, color: '#101623' }}>{paidPlan.name}</span>
                </div>
                <div style={{ fontSize: 14, color: '#4A5262' }}>
                  <span style={{ fontSize: 26, fontWeight: 700, color: '#101623' }}>{formatPlanPrice(paidPlan.price)}</span>
                  <span style={{ marginLeft: 6 }}>{formatPriceSuffix(paidPlan.price)}</span>
                </div>
                <FeatureList
                  features={paidPlan.features}
                  icon={<Check size={18} color="var(--brand-600)" style={{ flexShrink: 0, marginTop: 1 }} />}
                />
                <Button fullWidth onClick={startCheckout} disabled={redirecting} leadingIcon={<Lock width={15} height={15} />}>
                  {redirecting ? 'Wird gestartet …' : `${paidPlan.name} buchen`}
                </Button>
                <p style={{ fontSize: 12, color: '#8A93A3', margin: '12px 0 0', textAlign: 'center' }}>
                  Monatlich kündbar über das Verwaltungsportal.
                </p>
                {status === 'past_due' && (
                  <Button variant="secondary" fullWidth onClick={openPortal} disabled={redirecting} style={{ marginTop: 10 }}>
                    Zahlungsdaten aktualisieren
                  </Button>
                )}
              </Card>
            </div>
          </>
        )}
      </div>

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </Layout>
  );
}

export default PricingPage;
